import React, { useEffect, useState } from 'react';
import {
  ArrowOutward as ArrowOutwardIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LockReset as LockResetIcon,
  MarkEmailRead as MarkEmailReadIcon,
  RefreshRounded as RefreshRoundedIcon,
  SendRounded as SendRoundedIcon,
  SupportAgent as SupportAgentIcon,
  TaskAlt as TaskAltIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { sendRequest } from '../../utils/dateHelpers';
import BrandedSelect from '../Form/BrandedSelect';
import Loading from '../Loading';
import { SegmentedControl } from '../ui';
const FALLBACK_TOPIC_OPTIONS = [
  {
    key: 'git_provider_integration',
    label: 'Git Provider Integration'
  },
  {
    key: 'seats_user_management',
    label: 'Seats and User Management'
  },
  {
    key: 'plan_configuration',
    label: 'Plan Configuration'
  },
  {
    key: 'billing',
    label: 'Billing and Invoicing'
  },
  {
    key: 'refund_request',
    label: 'Refund Request'
  },
  {
    key: 'historical_analysis',
    label: 'Historical Analysis'
  },
  {
    key: 'bug_report',
    label: 'Bug Report'
  },
  {
    key: 'other',
    label: 'Other'
  }
];
const STATUS_META = {
  waiting_for_support: {
    label: 'Waiting for support',
    description: 'The request is waiting for a reply from GitMe Support.',
    chip: 'border-sky-200 bg-sky-50 text-sky-700'
  },
  waiting_for_customer: {
    label: 'Waiting for customer',
    description: 'GitMe Support replied and is waiting for the customer.',
    chip: 'border-violet-200 bg-violet-50 text-violet-700'
  },
  resolved: {
    label: 'Resolved',
    description:
      'The issue appears solved, but the request can still be reopened.',
    chip: 'border-emerald-200 bg-emerald-50 text-emerald-700'
  },
  closed: {
    label: 'Closed',
    description: 'The request is closed and can be reopened later if needed.',
    chip: 'border-slate-200 bg-slate-100 text-slate-700'
  }
};
const PANEL_CLASS =
  'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900';
const SUBTLE_PANEL_CLASS =
  'rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/50';
const INPUT_CLASS =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#3C50E0] focus:ring-2 focus:ring-[#3C50E0]/15 dark:border-slate-700 dark:bg-slate-950 dark:text-white';
const PRIMARY_BUTTON_CLASS =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#3C50E0] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#3345c4] disabled:cursor-not-allowed disabled:opacity-60';
const SECONDARY_BUTTON_CLASS =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
const formatDateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};
const getStatusMeta = status =>
  STATUS_META[status] || {
    label: status || 'Unknown',
    description: '',
    chip: 'border-slate-200 bg-slate-100 text-slate-700'
  };
const getCustomerSupportPrefill = () => {
  if (typeof window === 'undefined') {
    return {
      topicKey: '',
      message: ''
    };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    topicKey:
      params.get('topic') === 'historical_analysis'
        ? 'historical_analysis'
        : '',
    message: String(params.get('message') || '').slice(0, 5000)
  };
};
const SupportWorkspace = () => {
  const [supportPrefill] = useState(() => getCustomerSupportPrefill());
  const apiBaseUrl = '/support/requests';
  const buildRequestDetailUrl = requestId => `${apiBaseUrl}/${requestId}`;
  const buildReplyUrl = requestId => `${apiBaseUrl}/${requestId}/messages`;
  const buildStatusUrl = requestId => `${apiBaseUrl}/${requestId}/status`;
  const [topicOptions, setTopicOptions] = useState(FALLBACK_TOPIC_OPTIONS);
  const [requests, setRequests] = useState([]);
  const [expandedRequestId, setExpandedRequestId] = useState('');
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [topicKey, setTopicKey] = useState(
    supportPrefill.topicKey || FALLBACK_TOPIC_OPTIONS[0].key
  );
  const [requestMessage, setRequestMessage] = useState(supportPrefill.message);
  const [replyMessage, setReplyMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [customerTab, setCustomerTab] = useState('new');
  const loadRequestDetail = async requestId => {
    if (!requestId) {
      setExpandedRequest(null);
      setReplyMessage('');
      return;
    }
    setLoadingDetail(true);
    try {
      const response = await sendRequest(
        buildRequestDetailUrl(requestId),
        'GET'
      );
      setExpandedRequest(response?.request || null);
      setReplyMessage('');
    } catch (error) {
      toast.error(error?.message || 'Failed to load support request.');
      setExpandedRequest(null);
    } finally {
      setLoadingDetail(false);
    }
  };
  const loadRequests = async preferredRequestId => {
    setLoadingList(true);
    try {
      const response = await sendRequest(apiBaseUrl, 'GET');
      const nextRequests = response?.requests || [];
      const nextTopicOptions =
        response?.topicOptions?.length > 0
          ? response.topicOptions
          : FALLBACK_TOPIC_OPTIONS;
      setTopicOptions(nextTopicOptions);
      if (!nextTopicOptions.some(option => option.key === topicKey)) {
        setTopicKey(nextTopicOptions[0]?.key || FALLBACK_TOPIC_OPTIONS[0].key);
      }
      setRequests(nextRequests);
      const nextExpandedId =
        preferredRequestId &&
        nextRequests.some(item => item.id === preferredRequestId)
          ? preferredRequestId
          : expandedRequestId &&
              nextRequests.some(item => item.id === expandedRequestId)
            ? expandedRequestId
            : '';
      setExpandedRequestId(nextExpandedId);
      if (nextExpandedId) {
        await loadRequestDetail(nextExpandedId);
      } else {
        setExpandedRequest(null);
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to load support requests.');
      setRequests([]);
      setExpandedRequestId('');
      setExpandedRequest(null);
    } finally {
      setLoadingList(false);
    }
  };
  useEffect(() => {
    loadRequests();
  }, []);
  const refreshExpandedRequest = async requestId => {
    await loadRequests(requestId || expandedRequestId);
  };
  const handleCreateRequest = async event => {
    event.preventDefault();
    if (!requestMessage.trim()) {
      toast.error('Please describe your issue before submitting.');
      return;
    }
    setSubmittingRequest(true);
    try {
      const response = await sendRequest(apiBaseUrl, 'POST', {
        topicKey,
        message: requestMessage
      });
      const createdRequest = response?.request || null;
      setRequestMessage('');
      toast.success('Support request created successfully.');
      setCustomerTab('history');
      await refreshExpandedRequest(createdRequest?.id);
    } catch (error) {
      toast.error(error?.message || 'Failed to create support request.');
    } finally {
      setSubmittingRequest(false);
    }
  };
  const handleReply = async event => {
    event.preventDefault();
    if (!expandedRequest?.id) {
      return;
    }
    if (!replyMessage.trim()) {
      toast.error('Please add a message before sending.');
      return;
    }
    setSubmittingReply(true);
    try {
      await sendRequest(buildReplyUrl(expandedRequest.id), 'POST', {
        message: replyMessage
      });
      setReplyMessage('');
      toast.success('Reply sent successfully.');
      await refreshExpandedRequest(expandedRequest.id);
    } catch (error) {
      toast.error(error?.message || 'Failed to send the reply.');
    } finally {
      setSubmittingReply(false);
    }
  };
  const handleStatusUpdate = async nextStatus => {
    if (!expandedRequest?.id || !nextStatus) {
      return;
    }
    setUpdatingStatus(true);
    try {
      await sendRequest(buildStatusUrl(expandedRequest.id), 'PATCH', {
        status: nextStatus
      });
      toast.success('Request status updated.');
      await refreshExpandedRequest(expandedRequest.id);
    } catch (error) {
      toast.error(error?.message || 'Failed to update request status.');
    } finally {
      setUpdatingStatus(false);
    }
  };
  const filteredRequests = requests.filter(request => {
    if (!searchQuery.trim()) {
      return true;
    }
    const haystack = [
      request.requestNumber,
      request.companyName,
      request.topicLabel,
      request.lastMessagePreview
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(searchQuery.trim().toLowerCase());
  });
  const canCustomerReopen =
    expandedRequest && ['resolved', 'closed'].includes(expandedRequest.status);
  const canCustomerResolve =
    expandedRequest && !['resolved', 'closed'].includes(expandedRequest.status);
  const isCustomerReplyLocked =
    expandedRequest && ['resolved', 'closed'].includes(expandedRequest.status);
  const topicSelectOptions = topicOptions.map(option => ({
    value: option.key,
    label: option.label
  }));
  const toggleAccordion = async requestId => {
    if (expandedRequestId === requestId) {
      setExpandedRequestId('');
      setExpandedRequest(null);
      setReplyMessage('');
      return;
    }
    setExpandedRequestId(requestId);
    await loadRequestDetail(requestId);
  };
  const renderExpandedContent = () => {
    if (!expandedRequestId) {
      return null;
    }
    if (loadingDetail || !expandedRequest) {
      return <Loading type="card" />;
    }
    return (
      <div className="space-y-4 border-t border-slate-200 pt-4 dark:border-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
              {expandedRequest.topicLabel}
            </div>
            <h3 className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
              {expandedRequest.requestNumber}
            </h3>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
              <span>Created {formatDateTime(expandedRequest.createdAt)}</span>

              <span>Opened by {expandedRequest.createdBy?.displayName}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${getStatusMeta(expandedRequest.status).chip}`}
            >
              {getStatusMeta(expandedRequest.status).label}
            </span>

            {
              <div className="flex flex-wrap gap-2">
                {canCustomerReopen && (
                  <button
                    type="button"
                    className={SECONDARY_BUTTON_CLASS}
                    disabled={updatingStatus}
                    onClick={() => handleStatusUpdate('waiting_for_support')}
                  >
                    <LockResetIcon
                      sx={{
                        fontSize: 18
                      }}
                    />
                    Reopen request
                  </button>
                )}
                {canCustomerResolve && (
                  <button
                    type="button"
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/60"
                    disabled={updatingStatus}
                    onClick={() => handleStatusUpdate('resolved')}
                  >
                    <TaskAltIcon
                      sx={{
                        fontSize: 18
                      }}
                    />
                    Mark as resolved
                  </button>
                )}
              </div>
            }
          </div>
        </div>

        <div className={`grid gap-3 ${'lg:grid-cols-3'}`}>
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-800 dark:bg-slate-950/50">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
              <MarkEmailReadIcon
                sx={{
                  fontSize: 18
                }}
              />
              Current status
            </div>
            <div className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
              {getStatusMeta(expandedRequest.status).description}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-800 dark:bg-slate-950/50">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
              <RefreshRoundedIcon
                sx={{
                  fontSize: 18
                }}
              />
              Last activity
            </div>
            <div className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
              {formatDateTime(expandedRequest.lastMessageAt)}
            </div>
          </div>
        </div>

        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-950/40">
          {expandedRequest.messages.length === 0 ? (
            <div className="text-sm text-slate-500 dark:text-slate-400">
              No messages yet.
            </div>
          ) : (
            expandedRequest.messages.map(message => {
              const isSupportMessage = message.senderType === 'support';
              return (
                <div
                  key={message.id}
                  className={`flex ${isSupportMessage ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-xl px-3.5 py-3 shadow-sm ${isSupportMessage ? 'border border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white' : 'bg-[#3C50E0] text-white'}`}
                  >
                    <div
                      className={`text-xs font-semibold uppercase tracking-[0.18em] ${isSupportMessage ? 'text-slate-400 dark:text-slate-500' : 'text-sky-100/90'}`}
                    >
                      {message.author?.displayName}
                    </div>
                    <div className="mt-2 whitespace-pre-wrap text-sm leading-6">
                      {message.body}
                    </div>
                    <div
                      className={`mt-3 text-xs ${isSupportMessage ? 'text-slate-400 dark:text-slate-500' : 'text-sky-100/85'}`}
                    >
                      {formatDateTime(message.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <form className="space-y-3" onSubmit={handleReply}>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Reply to this request
          </label>
          <textarea
            className={`${INPUT_CLASS} min-h-[140px] resize-y`}
            placeholder={
              isCustomerReplyLocked
                ? 'Reopen this request first if you want to continue the conversation.'
                : 'Add more detail or answer the latest support message.'
            }
            value={replyMessage}
            onChange={event => setReplyMessage(event.target.value)}
            disabled={isCustomerReplyLocked}
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              className={PRIMARY_BUTTON_CLASS}
              disabled={submittingReply || isCustomerReplyLocked}
            >
              <SendRoundedIcon
                sx={{
                  fontSize: 18
                }}
              />
              {submittingReply ? <Loading type="inline" /> : 'Send reply'}
            </button>

            {expandedRequest.statusHistory?.length > 1 && (
              <div className="text-xs text-slate-400 dark:text-slate-500">
                {expandedRequest.statusHistory.length} status change(s) recorded
              </div>
            )}
          </div>
        </form>
      </div>
    );
  };
  const renderAccordionList = () => (
    <div className="space-y-2.5">
      {loadingList ? (
        <Loading type="table" />
      ) : filteredRequests.length === 0 ? (
        <div className={SUBTLE_PANEL_CLASS}>{'No support requests yet.'}</div>
      ) : (
        filteredRequests.map(request => {
          const status = getStatusMeta(request.status);
          const isExpanded = request.id === expandedRequestId;
          return (
            <div
              key={request.id}
              className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950"
            >
              <button
                type="button"
                onClick={() => toggleAccordion(request.id)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition ${isExpanded ? 'bg-[#3C50E0]/5' : 'hover:bg-slate-50 dark:hover:bg-slate-900'}`}
              >
                <div className="min-w-0 flex flex-1 items-center gap-2 overflow-hidden">
                  <div className="shrink-0 text-sm font-semibold text-slate-900 dark:text-white">
                    {request.topicLabel}
                  </div>
                  <span
                    className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${status.chip}`}
                  >
                    {status.label}
                  </span>

                  <div className="min-w-0 flex-1 truncate text-sm text-slate-500 dark:text-slate-400">
                    Opened by {request.createdBy?.displayName || 'Unknown user'}
                  </div>
                </div>
                <div className="hidden shrink-0 items-center gap-3 text-xs text-slate-400 dark:text-slate-500 lg:flex">
                  <span className="shrink-0">{request.requestNumber}</span>
                  <span className="shrink-0">
                    {formatDateTime(request.lastMessageAt)}
                  </span>
                  <span className="shrink-0">{request.messageCount} msg</span>
                </div>
                <div className="flex min-w-0 shrink-0 items-center gap-2 lg:hidden">
                  <span className="max-w-[120px] truncate text-[11px] text-slate-400 dark:text-slate-500">
                    {request.requestNumber}
                  </span>
                  <span className="text-[11px] text-slate-400 dark:text-slate-500">
                    {request.messageCount}
                  </span>
                </div>
                <span className="shrink-0 text-slate-400 dark:text-slate-500">
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </span>
              </button>
              {isExpanded && (
                <div className="px-3 pb-3">{renderExpandedContent()}</div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
  return (
    <div className="space-y-6">
      {
        <>
          <div className={PANEL_CLASS}>
            <SegmentedControl
              items={[
                {
                  value: 'new',
                  label: 'New Request'
                },
                {
                  value: 'history',
                  label: 'Requests'
                }
              ]}
              value={customerTab}
              onChange={setCustomerTab}
            />
          </div>

          {customerTab === 'new' ? (
            <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
              <div className={PANEL_CLASS}>
                <div className="inline-flex items-center gap-2 rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-sky-700">
                  <SupportAgentIcon
                    sx={{
                      fontSize: 16
                    }}
                  />
                  Customer Support
                </div>
                <h3 className="mt-4 text-2xl font-semibold text-slate-900 dark:text-white">
                  Open a support request
                </h3>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Choose the topic that fits best and tell us what is happening
                  in as much detail as you need. Our support team will review
                  your request and reply as soon as possible inside this
                  workspace.
                </p>

                <form className="mt-6 space-y-4" onSubmit={handleCreateRequest}>
                  <BrandedSelect
                    label="Topic"
                    helperText="Choose the area that best matches your request so the right team can review it quickly."
                    value={topicKey}
                    onChange={setTopicKey}
                    options={topicSelectOptions}
                    placeholder="Select a topic"
                  />

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                      Tell us about the issue
                    </label>
                    <textarea
                      className={`${INPUT_CLASS} min-h-[200px] resize-y`}
                      placeholder="Describe the issue, what you expected, what happened instead, and any context that could help our team."
                      value={requestMessage}
                      onChange={event => setRequestMessage(event.target.value)}
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="submit"
                      className={PRIMARY_BUTTON_CLASS}
                      disabled={submittingRequest}
                    >
                      <SendRoundedIcon
                        sx={{
                          fontSize: 18
                        }}
                      />
                      {submittingRequest ? (
                        <Loading type="inline" />
                      ) : (
                        'Submit request'
                      )}
                    </button>
                    <a
                      href="https://docs.gitdotme.com/docs/intro"
                      target="_blank"
                      rel="noreferrer"
                      className={SECONDARY_BUTTON_CLASS}
                    >
                      View docs
                      <ArrowOutwardIcon
                        sx={{
                          fontSize: 18
                        }}
                      />
                    </a>
                  </div>
                </form>
              </div>

              <div
                className={`${PANEL_CLASS} bg-gradient-to-br from-slate-950 via-slate-900 to-[#243587] text-white dark:border-slate-700`}
              >
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-200/90">
                  Need a faster answer?
                </div>
                <h3 className="mt-4 text-2xl font-semibold">
                  Start with our documentation
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-200/85">
                  For common setup and product questions, the docs usually
                  provide the quickest path. If the docs do not solve it, keep
                  the request open and continue the thread with our team.
                </p>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-sm font-semibold">What to include</div>
                    <div className="mt-2 text-sm leading-6 text-slate-300">
                      Mention the workflow, what you expected, what blocked you,
                      and any relevant screenshots or exact wording from the
                      app.
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-sm font-semibold">
                      After submission
                    </div>
                    <div className="mt-2 text-sm leading-6 text-slate-300">
                      You can track your request and follow our replies from
                      this page, then close or reopen the conversation whenever
                      needed.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={PANEL_CLASS}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                    Requests
                  </div>
                  <h3 className="mt-2 text-xl font-semibold text-slate-900 dark:text-white">
                    Your support conversations
                  </h3>
                </div>

                <button
                  type="button"
                  className={SECONDARY_BUTTON_CLASS}
                  onClick={() => refreshExpandedRequest()}
                  disabled={loadingList || loadingDetail}
                >
                  <RefreshRoundedIcon
                    sx={{
                      fontSize: 18
                    }}
                  />
                  Refresh
                </button>
              </div>

              {requests.length > 6 && (
                <div className="mt-5">
                  <input
                    className={INPUT_CLASS}
                    placeholder="Search your requests"
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.target.value)}
                  />
                </div>
              )}

              <div className="mt-5">{renderAccordionList()}</div>
            </div>
          )}
        </>
      }
    </div>
  );
};
export const CustomerSupportPage = () => <SupportWorkspace />;
export default CustomerSupportPage;
